import type {
  Client,
  CoachProfile,
  Program,
  Quiz,
  UserGroup,
} from '@efdigital/ll-addon-data';
import type { Coaching } from '@efdigital/ll-addon-data/types/coaching/coaching';
import type { CoachingConfig } from '@efdigital/ll-addon-data/types/coaching/coaching-config';
import type { Cohort } from '@efdigital/ll-addon-data/types/xceed/cohort';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { EditCoachPoolMembers } from '@efdigital/ll-admin-v2/components/coaching/edit-coach-pool-members.gts';
import type { EditingContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { setAsyncBelongsTo } from '@efdigital/ll-admin-v2/data/async-setter';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save';
import { generateCoaching } from '@efdigital/ll-admin-v2/data/generators/coaching-generator.ts';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface EditCohortCoachingSignature {
  Args: {
    cohort: Cohort;
    program: Program;
    coaching: Coaching | null;
    quizId: string | null;
    coachingConfigs: CoachingConfig[];
    enrolledCount: number;
    coachPool: UserGroup | null;
    coaches: CoachProfile[];
    client: Client | null;
    context: EditingContext;
    /** When false, the form is read-only and the Edit control is hidden. */
    canEdit?: boolean;
  };
}

export class EditCohortCoaching extends Component<EditCohortCoachingSignature> {
  @service declare store: Store;
  @service declare router: RouterService;
  @tracked private _localCoaching: Coaching | null = null;
  @tracked private _settingUp = false;
  @tracked private _localPool: UserGroup | null = null;
  @tracked private _localQuiz: Quiz | null = null;
  @tracked private _pendingConfigId: string = '';
  @tracked private _removing = false;
  @tracked private _removed = false;
  @tracked private _error: string | null = null;

  get _coaching(): Coaching | null {
    if (this._removed) {
      return null;
    }
    return this._localCoaching ?? this.args.coaching;
  }

  // --- Coaching config ---
  get selectedConfigId(): string {
    return this._coaching?.coachingConfig?.id ?? '';
  }

  isSelectedConfig = (configId: string | null): boolean =>
    configId === this.selectedConfigId;

  setCoachingConfig = (event: Event) => {
    if (!this._coaching) {
      return;
    }
    const target = event.target as HTMLSelectElement;
    if (!target.value) {
      setAsyncBelongsTo(this._coaching, 'coachingConfig', null);
      return;
    }
    const selected = this.args.coachingConfigs.find(
      (c) => c.id === target.value
    );
    if (selected) {
      setAsyncBelongsTo(this._coaching, 'coachingConfig', selected);
    }
  };

  // --- Coach pool (a per-cohort UserGroup on this coaching) ---
  get pool(): UserGroup | null {
    return this._localPool ?? this.args.coachPool;
  }

  // --- Onboarding quiz ---
  get quizId(): string {
    return this._localQuiz?.id ?? this.args.quizId ?? '';
  }

  get _cantSetup(): boolean {
    return this._settingUp || !this._pendingConfigId;
  }

  setPendingConfig = (event: Event) => {
    this._pendingConfigId = (event.target as HTMLSelectElement).value;
  };

  setupCoaching = async () => {
    const config = this.args.coachingConfigs.find(
      (c) => c.id === this._pendingConfigId
    );
    if (!config) {
      return;
    }
    this._settingUp = true;
    this._error = null;
    try {
      const { coaching, pool, quiz } = await generateCoaching(
        this.args.cohort,
        config,
        { store: this.store }
      );
      this._removed = false;
      this._localCoaching = coaching;
      this._localPool = pool;
      this._localQuiz = quiz;
      // Re-fetch from the server as the source of truth — a locally-held
      // record can occasionally fail to reflect in the UI even though the
      // setup itself succeeded server-side.
      await this.router.refresh('programs.program.cohort.coaching');
    } catch (error) {
      console.error('Failed to set up coaching:', error);
      this._error =
        'Something went wrong setting up coaching. Please try again.';
    } finally {
      this._settingUp = false;
    }
  };

  removeCoaching = async () => {
    if (!this._coaching) {
      return;
    }
    if (
      !confirm(
        'Remove coaching from this cohort? The coaching tile will no longer appear for learners.'
      )
    ) {
      return;
    }
    this._removing = true;
    this._error = null;
    try {
      setAsyncBelongsTo(this.args.cohort, 'coaching', null);
      await deepSave(this.args.cohort, this.store, { maxDepth: 1 });
      this._removed = true;
      this._localCoaching = null;
      this._localPool = null;
      this._localQuiz = null;
      await this.router.refresh('programs.program.cohort.coaching');
    } catch (error) {
      console.error('Failed to remove coaching:', error);
      this._error = 'Something went wrong removing coaching. Please try again.';
    } finally {
      this._removing = false;
    }
  };

  <template>
    {{#if this._error}}
      <div class="alert alert-danger" role="alert">{{this._error}}</div>
    {{/if}}
    {{#if this._coaching}}
      <EditRecord
        @editLabel="Edit Coaching"
        @item={{this._coaching}}
        @context={{@context}}
        @canEdit={{@canEdit}}
        @maxDepth={{2}}
        ...attributes
      >
        <:form as |uniqueId isReadOnly|>
          <fieldset disabled={{isReadOnly}} class="mb-4">
            <legend class="gud-eyebrow">Coaching Configuration</legend>

            <div class="mb-3">
              <label
                for="{{uniqueId}}-coachingConfig"
                class="form-label"
              >Configuration Template</label>
              <select
                class="form-select"
                id="{{uniqueId}}-coachingConfig"
                {{debounce this.setCoachingConfig}}
              >
                <option value="">— None selected —</option>
                {{#each @coachingConfigs as |config|}}
                  <option
                    value={{config.id}}
                    selected={{this.isSelectedConfig config.id}}
                  >{{config.name}}</option>
                {{/each}}
              </select>
              <div class="form-text">Select a coaching configuration template to
                apply to this cohort.</div>
            </div>
          </fieldset>

          <fieldset disabled={{isReadOnly}} class="mb-4">
            <legend class="gud-eyebrow">Coach Pool</legend>
            <p class="form-text mt-0 mb-3">The coaches picked for this cohort.
              Coachees are matched from this pool.</p>

            {{#if this.pool}}
              <EditCoachPoolMembers
                @pool={{this.pool}}
                @coaches={{@coaches}}
                @readonly={{isReadOnly}}
              />
            {{/if}}
          </fieldset>

          <fieldset class="mb-4">
            <legend class="gud-eyebrow">Onboarding Quiz</legend>

            {{#if this.quizId}}
              {{#unless isReadOnly}}
                <LinkTo
                  @route="programs.program.cohort.content.quiz"
                  @models={{array @program.id @cohort.id this.quizId}}
                  class="btn btn-sm btn-outline-primary"
                >Edit quiz</LinkTo>
              {{/unless}}
            {{else}}
              <p class="text-muted small mb-0">No onboarding quiz for this
                coaching.</p>
            {{/if}}
          </fieldset>

          <fieldset class="mb-4">
            <legend class="gud-eyebrow">Enrolled Coachees</legend>

            <p class="text-muted small mb-2">{{@enrolledCount}}
              enrolled. View each coachee's coach partnerships and session
              history.</p>
            <LinkTo
              @route="programs.program.cohort.coaching.coachees"
              class="btn btn-outline-secondary btn-sm"
            >
              View coach partnerships &amp; session history →
            </LinkTo>
          </fieldset>

          {{#if @canEdit}}
            <fieldset class="mb-4">
              <legend class="gud-eyebrow">Remove Coaching</legend>

              <button
                type="button"
                class="btn btn-outline-danger btn-sm"
                disabled={{this._removing}}
                {{on "click" this.removeCoaching}}
              >
                {{if this._removing "Removing…" "Remove coaching"}}
              </button>
            </fieldset>
          {{/if}}
        </:form>
      </EditRecord>
    {{else}}
      <div class="py-4">
        <p class="text-muted mb-3">Coaching has not been set up for this cohort
          yet. Select a configuration template to get started.</p>
        <div class="mb-3 col-md-6 col-lg-4 p-0">
          <label class="form-label" for="pending-coaching-config">Configuration
            Template</label>
          <select
            class="form-select"
            id="pending-coaching-config"
            {{on "change" this.setPendingConfig}}
          >
            <option value="">— Select a template —</option>
            {{#each @coachingConfigs as |config|}}
              <option value={{config.id}}>{{config.name}}</option>
            {{/each}}
          </select>
        </div>
        <button
          type="button"
          class="btn btn-primary"
          disabled={{this._cantSetup}}
          {{on "click" this.setupCoaching}}
        >
          {{if this._settingUp "Setting up…" "Set up coaching"}}
        </button>
      </div>
    {{/if}}
  </template>
}
